import { Injectable } from '@angular/core';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';

import { DeficiencyTicketsDaoService } from '../../dao/deficiency-tickets-dao.service';
import { DeficiencyTicketsService } from '../deficiency-tickets.service';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { TranslateService } from '@ngx-translate/core';
import { ExtFilesService } from 'src/app/_services/ext-files/logic/ext-files.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import {
  DeficiencyTicket,
  DeficiencyTicketDTO,
} from 'src/app/_model/deficiency-ticket';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads DeficiencyTickets
 */
@Injectable()
export class DeficiencyTicketsLogicService implements DeficiencyTicketsService {
  public deficiencyTickets$ = new BehaviorSubject<DeficiencyTicket[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  private temporaryLocalDeficiencyTickets?: DeficiencyTicket[];

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private remoteActionService: RemoteActionService,
    private deficiencyTicketsDaoService: DeficiencyTicketsDaoService,
    private authenticationService: AuthenticationService,
    private appParamService: AppParameterService,
    private localStatusService: LocalStatusService,
    private servicejobsService: ServicejobsService,
  ) {
    this.initialize();
  }

  // Public Functions
  public async createDeficiencyTicket(
    deficiencyTicket: DeficiencyTicket,
  ): Promise<DeficiencyTicket> {
    let newDeficiencyTicket =
      await this.deficiencyTicketsDaoService.createDeficiencyTicket(
        deficiencyTicket,
      );
    await this.loadLocalDeficiencyTickets();
    return newDeficiencyTicket;
  }

  public getDeficiencyTicketById(id: IMSId): DeficiencyTicket {
    if (id === undefined) {
      return undefined;
    }
    return this.deficiencyTickets$.getValue().find((deficiencyTicket) => {
      if (
        (deficiencyTicket.id.imsId == id.imsId &&
          deficiencyTicket.id.imsId != null) ||
        (deficiencyTicket.id.secondaryId == id.secondaryId &&
          deficiencyTicket.id.secondaryId != null)
      ) {
        return true;
      }
      return false;
    });
  }

  public getDeficiencyTicketByPinId(id: IMSId): DeficiencyTicket[] {
    if (id == undefined) {
      return [];
    }

    return this.deficiencyTickets$.getValue()?.filter((dt) => {
      return IMSObject.equals(dt?.pin?.id, id);
    }) ?? [];
  }

  public async updateDeficiencyTicket(deficiencyTicket: DeficiencyTicket) {
    let dts = this.deficiencyTickets$.getValue();
    for (let dt of dts) {
      if (
        dt.id.imsId === deficiencyTicket.id.imsId ||
        dt.id.secondaryId === deficiencyTicket.id.secondaryId
      ) {
        dt = deficiencyTicket;
      }
    }
    this.deficiencyTickets$.next(dts);
    await this.deficiencyTicketsDaoService.saveDeficiencyTickets(dts);
  }

  // Resetable
  public async reset() {
    this.deficiencyTicketsDaoService.reset();
    await this.loadLocalDeficiencyTickets();
  }

  public async clear() {
    await this.deficiencyTicketsDaoService.clear();
    this.deficiencyTickets$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.deficiencyTicketsDaoService.clearRemoteDeficiencyTickets();
    await this.loadRemoteDeficiencyTickets(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {
    this.temporaryLocalDeficiencyTickets = (this.deficiencyTickets$
      .getValue() ?? [])
      .filter((sjob) => {
        return sjob.isLocal();
      });
  }
  public async postsync(): Promise<void> {
    this.logger.log(
      this.constructor.name,
      'Executing postsync -> merge local deficiencytickets with remote deficiencytickets ',
      this.temporaryLocalDeficiencyTickets?.length,
      this.deficiencyTickets$.getValue()?.length,
    );

    // Check if a localdeficiencyticket can be found in the new synced dts.
    // if yes we can delete the local dt.
    for (const ldt of this.temporaryLocalDeficiencyTickets) {
      const found = this.deficiencyTickets$.getValue().find((dt) => {
        console.log(
          'JBSyncLocalDT compare',
          ldt.entityCreatedLocalDeviceId,
          '=',
          dt.entityCreatedLocalDeviceId,
          '&&',
          ldt.entityCreatedDeviceToken,
          '=',
          dt.entityCreatedDeviceToken,
        );
        return (
          ldt.entityCreatedLocalDeviceId === dt.entityCreatedLocalDeviceId &&
          ldt.entityCreatedDeviceToken === dt.entityCreatedDeviceToken
        );
      });

      if (found) {
        this.logger.log(
          this.constructor.name,
          'JBSyncLocalDT',
          'Found a remote deficiency ticket for local deficiency ticket',
          ldt.title,
          'delete the local duplicate',
        );
        await this.deficiencyTicketsDaoService.deleteLocalDeficiencyTicket(
          ldt.id,
        );
      } else {
        this.logger.log(
          this.constructor.name,
          'JBSyncLocalDT',
          'No copy of local deficiency ticket found',
          ldt.title,
          'keep local copy',
        );
      }
    }
    await this.loadLocalDeficiencyTickets();
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalDeficiencyTickets();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalDeficiencyTickets() {
    try {
      this.logger.log(this.constructor.name, '- Load local DeficiencyTickets');
      let deficiencyTickets: DeficiencyTicket[] =
        await this.deficiencyTicketsDaoService.getDeficiencyTickets();
      this.logger.log(
        this.constructor.name,
        '- Loaded local DeficiencyTickets',
        deficiencyTickets?.length,
        deficiencyTickets,
      );
      this.deficiencyTickets$.next(deficiencyTickets);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local DeficiencyTickets: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteDeficiencyTickets(apiLevel: APILevel, syncKey: string) {
    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new DeficiencyTickets from IMS',
      );

      try {
        const sjobList = this.servicejobsService.servicejobs$
          .getValue()
          .map((sjob) => {
            return sjob.getImsId();
          })
          .join(',');

        const data: any = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'syncdeficiencytickets',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              sjoblist: sjobList,
              synckey: syncKey
            },
          }),
        );
        this.logger.log(
          this.constructor.name,
          '- JB2021 Loaded new DeficiencyTickets from IMS ',
          data,
        );
        await this.parseData(data);
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading DeficiencyTickets: ',
          error?.message,
        );
        throw error;
      }
    }
  }

  private async parseData(data: any) {
    if (data.deficiencytickets) {
      await this.parseDeficiencyTicketsDTOArray(data.deficiencytickets);
    }
    if (data.servicejobs) {
      await this.servicejobsService.addDeficiencyTicketServicejobs(
        data.servicejobs,
      );
    }
  }

  private async parseDeficiencyTicketsDTOArray(
    deficiencyticketsDto: DeficiencyTicketDTO[],
  ) {
    let deficiencyTickets: DeficiencyTicket[] = [];

    for (const deficiencyticketDto of deficiencyticketsDto) {
      let localStatus =
        await this.localStatusService.loadLocalDeficiencyTicketStatus({
          imsId: deficiencyticketDto.id,
          secondaryId: undefined,
          objRef: 'ims/DeficiencyTicket',
        });
      deficiencyTickets.push(
        DeficiencyTicket.fromDto(
          deficiencyticketDto,
          localStatus,
          undefined,
          this.appParamService.getFormatLabelForObject('ims/DeficiencyTicket'),
        ),
      );
    }

    this.logger.log(
      this.constructor.name,
      'Downloaded',
      deficiencyTickets?.length,
      'DeficiencyTicket',
    );

    try {
      await this.deficiencyTicketsDaoService.saveDeficiencyTickets(
        deficiencyTickets,
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        '- Could not save service jobs',
        error?.message,
      );
    }

    this.loadLocalDeficiencyTickets();
  }

  async initializeCompleted(): Promise<void> {}
}
